import { useEffect, useMemo, useRef, useState } from "react";
import { Check, ChevronsUpDown, Loader2 } from "lucide-react";
import { cn } from "@project/lib/utils";
import { Button } from "@project/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@project/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@project/components/ui/popover";

export type SearchableSelectOption = {
  value: string;
  label: string;
  secondary?: string;
};

export type SearchableSelectProps = {
  value?: string;
  onValueChange: (value: string, option?: SearchableSelectOption) => void;
  options?: SearchableSelectOption[];
  onSearch?: (query: string) => Promise<SearchableSelectOption[]>;
  seedOption?: SearchableSelectOption;
  placeholder?: string;
  searchPlaceholder?: string;
  emptyText?: string;
  disabled?: boolean;
  className?: string;
};

export function SearchableSelect({
  value,
  onValueChange,
  options = [],
  onSearch,
  seedOption,
  placeholder = "Select...",
  searchPlaceholder = "Search...",
  emptyText = "No results found.",
  disabled = false,
  className,
}: SearchableSelectProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [asyncOptions, setAsyncOptions] = useState<SearchableSelectOption[]>(
    []
  );
  const [loading, setLoading] = useState(false);

  const staticOptions = useMemo(
    () => [...options].sort((a, b) => a.label.localeCompare(b.label)),
    [options]
  );

  useEffect(() => {
    if (!onSearch || !open) {
      return;
    }

    let ignore = false;
    const timer = window.setTimeout(async () => {
      setLoading(true);
      try {
        const results = await onSearch(search.trim());
        if (!ignore) {
          setAsyncOptions(results);
        }
      } finally {
        if (!ignore) {
          setLoading(false);
        }
      }
    }, 300);

    return () => {
      ignore = true;
      window.clearTimeout(timer);
    };
  }, [onSearch, open, search]);

  const displayOptions = useMemo(() => {
    const base = onSearch ? asyncOptions : staticOptions;
    if (seedOption && !base.some(o => o.value === seedOption.value)) {
      return [seedOption, ...base];
    }
    return base;
  }, [onSearch, asyncOptions, staticOptions, seedOption]);

  const selected = useMemo(() => {
    const fromList = displayOptions.find(o => o.value === value);
    if (fromList) {
      return fromList;
    }
    if (seedOption?.value === value) {
      return seedOption;
    }
    return (
      staticOptions.find(o => o.value === value) ??
      asyncOptions.find(o => o.value === value)
    );
  }, [displayOptions, staticOptions, asyncOptions, seedOption, value]);

  const handleOpenChange = (next: boolean) => {
    setOpen(next);
    if (!next) {
      setSearch("");
    }
  };

  const listRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) {
      return;
    }
    const el = listRef.current;
    if (!el) {
      return;
    }

    const onWheel = (e: Event) => {
      const wheel = e as globalThis.WheelEvent;
      wheel.stopPropagation();
      if (el.scrollHeight <= el.clientHeight) {
        return;
      }

      const delta = wheel.deltaY;
      const atTop = el.scrollTop <= 0;
      const atBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 1;

      if ((delta < 0 && atTop) || (delta > 0 && atBottom)) {
        wheel.preventDefault();
        return;
      }

      wheel.preventDefault();
      el.scrollTop += delta;
    };

    el.addEventListener("wheel", onWheel, { passive: false });
    return () => el.removeEventListener("wheel", onWheel);
  }, [open, loading, displayOptions.length]);

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          className={cn(
            "bg-muted/40 hover:bg-muted/60 h-10 w-full justify-between rounded-xl border-transparent font-normal",
            !value && "text-muted-foreground",
            className
          )}
        >
          <span className="truncate text-left">
            {selected
              ? selected.secondary
                ? `${selected.label} — ${selected.secondary}`
                : selected.label
              : placeholder}
          </span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="z-[60] w-[var(--radix-popover-trigger-width)] rounded-xl p-0"
        align="start"
        onOpenAutoFocus={e => e.preventDefault()}
        onWheel={e => e.stopPropagation()}
      >
        <Command shouldFilter={!onSearch}>
          <CommandInput
            placeholder={searchPlaceholder}
            value={search}
            onValueChange={setSearch}
          />
          <CommandList
            ref={listRef}
            className="max-h-[280px] overflow-y-auto overscroll-y-contain"
          >
            {loading ? (
              <div className="flex items-center justify-center gap-2 py-8 text-sm text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" />
                Loading…
              </div>
            ) : (
              <>
                <CommandEmpty>{emptyText}</CommandEmpty>
                <CommandGroup>
                  {displayOptions.map(option => (
                    <CommandItem
                      key={option.value}
                      value={
                        option.secondary
                          ? `${option.label} ${option.secondary}`
                          : option.label
                      }
                      keywords={[
                        option.label,
                        option.secondary ?? "",
                        option.value,
                      ]}
                      onSelect={() => {
                        onValueChange(option.value, option);
                        setOpen(false);
                        setSearch("");
                      }}
                      className="cursor-pointer"
                    >
                      <Check
                        className={cn(
                          "mr-2 h-4 w-4 shrink-0",
                          value === option.value ? "opacity-100" : "opacity-0"
                        )}
                      />
                      <span className="truncate">
                        {option.label}
                        {option.secondary ? (
                          <span className="text-muted-foreground">
                            {" "}
                            — {option.secondary}
                          </span>
                        ) : null}
                      </span>
                    </CommandItem>
                  ))}
                </CommandGroup>
              </>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
